import Button from "@components/common/Button";
import Input from "@components/common/Input";
import {
  HubSporFormGroupOneFieldModel,
  HubSpotFormDataModel,
  HubspotFormFieldType,
} from "@models/strapi-components/hubspot";
import React, { useState } from "react";
import Textarea from "./TextArea";
import SelectComponent, { OptionType } from "./Select";
import { produce } from "immer";
import { SingleValue } from "react-select";
import useHubSpotForm from "../../hooks/hubspot";
import PhoneInput from "react-phone-number-input";
import RadioGroup, { RadioOptionModel } from "./RadioGroup";
import useIpAddress from "@hooks/use-ip";
import { useCookies } from "react-cookie";
import { FaAsterisk } from "react-icons/fa";
import { twMerge } from "tailwind-merge";
import { callAllEach } from "@utils/common";
export interface HubSportFormModel {
  hubspotForm?: HubSpotFormDataModel;
  pageUrl: string;
  btnClassName?: string;
  formFieldsWrapper?: string;
  callback?: () => void;
}

const HubSpotForm: React.FunctionComponent<HubSportFormModel> = ({
  hubspotForm,
  pageUrl,
  btnClassName = "",
  formFieldsWrapper = "",
  callback = () => {},
}) => {
  const { portalId, guid, formFieldGroups, submitText } = hubspotForm ?? {};
  const [formState, setFormState] = useState<Record<string, unknown>>({});
  const { submitForm } = useHubSpotForm(
    portalId ?? "",
    guid ?? "",
    callAllEach(resetForm, callback)
  );
  const { geoLocationData } = useIpAddress();
  const [cookies] = useCookies(["hubspotutk"]);
  const hubspotTrackingCookie = cookies?.hubspotutk ?? "";

  const ipAddress = geoLocationData?.ip ?? "";

  const flatFormFields = formFieldGroups?.reduce(function (
    acc: HubSporFormGroupOneFieldModel[],
    curr
  ) {
    const { fields = [] } = curr;
    // eslint-disable-next-line prefer-spread
    acc.push.apply(acc, fields);
    return acc;
  },
  []);

  function resetForm() {
    setFormState({});
  }

  const handleInputChange = (name: string, value: string) => {
    const newState = produce(formState, (draft) => {
      draft[name] = value;
    });
    setFormState(newState);
  };

  const handleSelectChange = (name: string, value: SingleValue<OptionType>) => {
    const newState = produce(formState, (draft) => {
      draft[name] = value;
    });
    setFormState(newState);
  };

  const handlePhoneNumberChange = (name: string, value: string) => {
    const newState = produce(formState, (draft) => {
      draft[name] = value;
    });
    setFormState(newState);
  };

  const handleRadioChange = (name: string, value: string) => {
    const newState = produce(formState, (draft) => {
      draft[name] = value;
    });
    setFormState(newState);
  };

  const handleFormSubmission = async () => {
    const formSubmitfields: unknown[] = [];
    flatFormFields?.forEach((field) => {
      const { objectTypeId, name } = field ?? {};
      const formStateValue = formState?.[name];
      if (!formStateValue) return;
      let val = formStateValue;
      if (typeof formStateValue === "object") {
        val = (formStateValue as OptionType).value;
      }
      const temp = {
        objectTypeId: objectTypeId,
        name: name,
        value: val,
      };
      formSubmitfields.push(temp);
    });
    const payload = {
      submittedAt: new Date().valueOf(),
      fields: formSubmitfields,
      legalConsentOptions: {
        consent: {
          consentToProcess: true,
          text: "I agree to allow UNBXD to store and process my personal data.",
          communications: [
            {
              value: true,
              subscriptionTypeId: 999,
              text: "I agree to receive marketing communications from UNBXD.",
            },
          ],
        },
      },
      context: {
        pageUri: pageUrl,
        ipAddress,
        hutk: hubspotTrackingCookie,
      },
    };
    await submitForm(payload);
  };

  const renderField = (field: HubSporFormGroupOneFieldModel) => {
    const { fieldType, name, label, required, placeholder, options } = field;
    let inputComponent = <></>;
    switch (fieldType) {
      case HubspotFormFieldType.TEXT:
        inputComponent = (
          <Input
            label={label}
            className="text-sm"
            placeholder={placeholder}
            isRequired={required}
            name={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleInputChange(name, e.target.value);
            }}
            value={(formState?.[name] as string) ?? ""}
          />
        );
        break;
      case HubspotFormFieldType.TEXTAREA:
        inputComponent = (
          <Textarea
            labelText={label}
            required={required}
            name={name}
            placeholder={placeholder}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              handleInputChange(name, e.target.value);
            }}
            value={formState?.[name] as string}
          />
        );
        break;
      case HubspotFormFieldType.SELECT:
        inputComponent = (
          <SelectComponent<false>
            label={label}
            options={options}
            name={name}
            placeholder={placeholder}
            required={required}
            onChange={(val) => {
              handleSelectChange(name, val);
            }}
            defaultValue={options?.[0]}
          />
        );
        break;
      case HubspotFormFieldType.PHONENUMBER:
        inputComponent = (
          <div className="relative">
            <div className="flex justify-start items-start mb-1.5">
              {label ? <label className="text-white">{label}</label> : <></>}
              {required ? (
                <FaAsterisk className="fill-primary-red-50 pt-1" size={12} />
              ) : (
                <></>
              )}
            </div>
            <PhoneInput
              className="w-full pr-0"
              placeholder="Enter phone number"
              defaultCountry="US"
              onChange={(val) => {
                handlePhoneNumberChange(name, val ?? "");
              }}
            />
          </div>
        );
        break;
      case HubspotFormFieldType.RADIO:
        inputComponent = (
          <RadioGroup
            title={label}
            name={name}
            radioOptions={options as unknown as RadioOptionModel[]}
            labelClassName="text-white"
            isRequired={required}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleRadioChange(name, e.target.value)
            }
          />
        );
        break;
      default:
        break;
    }
    return inputComponent;
  };

  return (
    <div className="p-[30px] bg-dark-purple-50 w-full md:w-[675px] rounded-2xl">
      <div
        className={twMerge(
          "grid grid-cols-1 md:grid-cols-2 gap-7",
          formFieldsWrapper
        )}
      >
        {flatFormFields?.map((field) => (
          <div key={field.name}>{renderField(field)}</div>
        ))}
        {/* <Input
          label="Full Name"
          className="text-sm"
          placeholder="Enter your full name*"
          isRequired
        />
        <Input label="Company" placeholder="Enter your company name" />
        <Input
          label="Work Email"
          placeholder="Enter your work email"
          isRequired
        />
        <div className="relative">
          <span className="text-white">Phone Number</span>
          <PhoneInput
            className="w-full pr-0"
            placeholder="Enter phone number"
            defaultCountry="IN"
            onChange={(val) => {
            }}
          />
        </div> */}
      </div>
      <Button
        variant="primary"
        className={twMerge("mt-10 h-[43px] w-[243px] capitalize", btnClassName)}
        onClick={handleFormSubmission}
      >
        {(submitText ?? "SUBMIT")?.toUpperCase()}
      </Button>
    </div>
  );
};
export default HubSpotForm;
